import React, { useEffect, useState } from 'react';
import { Star, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

interface Review {
  id: string;
  rating: number;
  feedback: string;
  created_at: string;
  profiles: {
    display_name: string;
    profile_image_url: string | null;
  } | null;
}

interface AdvisorReviewsProps {
  advisorId: string;
  advisorName?: string;
  onReviewsUpdated?: () => void;
}

export default function AdvisorReviews({ advisorId, advisorName, onReviewsUpdated }: AdvisorReviewsProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchReviews();
  }, [advisorId]);

  const fetchReviews = async () => {
    try {
      console.log('Fetching reviews for advisor:', advisorId);
      setLoading(true);
      
      const { data, error: fetchError } = await supabase
        .from('advisor_ratings')
        .select(`
          id,
          rating,
          feedback,
          created_at,
          profiles!advisor_ratings_client_id_fkey (
            display_name,
            profile_image_url
          )
        `)
        .eq('advisor_id', advisorId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error fetching reviews:', fetchError);
        throw fetchError;
      }

      console.log('Fetched reviews:', data);
      setReviews(data || []);
      
      // Call the callback if provided
      if (onReviewsUpdated) {
        onReviewsUpdated();
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 py-4">
        {error}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        {advisorName ? `${advisorName} has no reviews yet` : 'No reviews yet'}
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-y-auto space-y-6">
      {reviews.map((review) => (
        <div key={review.id} className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center overflow-hidden">
                {review.profiles?.profile_image_url ? (
                  <img 
                    src={review.profiles.profile_image_url} 
                    alt="Client" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-6 h-6 text-purple-600" />
                )}
              </div>
              <div className="ml-4">
                <p className="font-medium">{review.profiles?.display_name || 'Anonymous Client'}</p>
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < review.rating
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500">
                  {format(new Date(review.created_at), 'MMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>
          {review.feedback && (
            <p className="text-gray-700">{review.feedback}</p>
          )}
        </div>
      ))}
    </div>
  );
}